import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getEventos, createEvento, updateEvento, deleteEvento } from "../api";
import { AuthContext } from "../AuthContext";
import { Pencil, Trash2, Sunset } from "lucide-react";

export default function Eventos() {
  const { user } = useContext(AuthContext);

  const [eventos, setEventos] = useState([]);
  const [descricao, setDescricao] = useState("");
  const [editingId, setEditingId] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [msg, setMsg] = useState(null);

  const navigate = useNavigate();

  // 🔹 Buscar eventos
  const fetchEventos = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await getEventos(user.token);
      setEventos(data);
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoadingList(false);
    }
  }, [user.token]);

  useEffect(() => {
    fetchEventos();
  }, [fetchEventos]);

  // 🔹 Criar / Atualizar
  async function handleSubmit(e) {
    e.preventDefault();
    setMsg(null);

    if (!descricao.trim()) {
      setMsg({ type: "error", text: "Descrição é obrigatória." });
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await updateEvento(editingId, user.token, { descricao });
        setMsg({ type: "success", text: "Evento atualizado!" });
      } else {
        await createEvento(user.token, { descricao });
        setMsg({ type: "success", text: "Evento criado!" });
      }

      setDescricao("");
      setEditingId(null);
      fetchEventos();
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }

  function handleSetEvento(evento_id) {
    localStorage.setItem("evento", evento_id);
    if (evento_id !== 0) {
      navigate("/dashboard");
    }
  }

  function handleResetEvento() {
    localStorage.setItem("evento", 0);
  }

  // 🔹 Editar
  function handleEdit(evento) {
    setDescricao(evento.descricao);
    setEditingId(evento.id);
    setMsg(null);
  }

  function handleCancelEdit() {
    setDescricao("");
    setEditingId(null);
  }

  // 🔹 Deletar
  async function handleDelete(id) {
    if (!window.confirm("Deseja excluir este evento?")) return;

    try {
      await deleteEvento(user.token, id);
      setMsg({ type: "success", text: "Evento excluído!" });
      setEventos(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    }
  }

return (
  <div className="page-container">
    <div className="dashboard-container">
      <div className="user-card" style={{ maxWidth: "1000px", margin: "0 auto" }}>

        {/* Cabeçalho */}
        <div style={{ 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "2rem"
        }}>
          <h3>{editingId ? "Editar Evento" : "Novo Evento"}</h3>
        </div>

        {/* ==================== FORMULÁRIO DE CADASTRO/EDIÇÃO ==================== */}
        <div className="item-row add-row" style={{ marginBottom: "2.5rem", gridTemplateColumns: "1fr 1fr auto" }}>
          <div className="item-input" style={{ gridColumn: "span 2" }}>
            <label>Descrição do Evento</label>
            <input
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Feira, Show, Congresso, Workshop..."
              className="input-text"
            />
          </div>

          <div className="item-actions">
            <button 
              type="button"
              className="btn-small"
              onClick={handleSubmit}
              disabled={loading || !descricao.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner" style={{ marginRight: "8px" }}></span>
                  Salvando...
                </>
              ) : editingId ? (
                "Salvar Alterações"
              ) : (
                "Cadastrar Evento"
              )}
            </button>
            {editingId && (
            <button 
              type="button" 
              className="btn-small"
              onClick={handleCancelEdit}
            >
              Cancelar Edição
            </button>
          )}
          </div>
        </div>
        <button 
          type="button"
          className="btn-small"
          onClick={handleResetEvento}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner" style={{ marginRight: "8px" }}></span>
              Salvando...
            </>
          ) : (
            "Resetar Evento"
          )}
        </button>

        {/* Mensagem de feedback */}
        {msg && (
          <div className={msg.type === "error" ? "error-msg" : "success-msg"} style={{ marginBottom: "2rem" }}>
            {msg.text}
          </div>
        )}

        {/* ==================== LISTA DE EVENTOS ==================== */}
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ marginBottom: "1.5rem" }}>Lista de Eventos</h3>

          {loadingList ? (
            <div className="center-content" style={{ padding: "3rem 0" }}>
              <div className="spinner"></div>
              <p>Carregando eventos...</p>
            </div>
          ) : (
            <div className="items-list">
              {eventos.length === 0 ? (
                <p style={{ 
                  opacity: 0.7, 
                  textAlign: "center", 
                  padding: "3rem 0",
                  fontSize: "1.05rem"
                }}>
                  Nenhum evento cadastrado ainda.
                </p>
              ) : (
                eventos.sort((a, b) => b.id - a.id).map((e) => (
                  <div key={e.id}
                    className="item-row"
                    style={{
                      background: editingId === e.id ? "rgba(59, 130, 246, 0.12)" : undefined,
                      border: editingId === e.id ? "1px solid #3b82f6" : undefined,
                      gridTemplateColumns: "1fr 1fr auto"
                    }}
                  >

                    <div className="item-input">
                      <label>Descrição</label>
                      <input value={e.descricao} disabled />
                    </div>

                    <div className="item-actions">
                      <button
                        className="btn-small btn-confirm"
                        onClick={() => handleSetEvento(e.id)}
                        title="Selecionar Evento"
                      >
                        <Sunset size={18} />
                      </button>

                      <button
                        className="btn-small"
                        onClick={() => handleEdit(e)}
                        title="Editar"
                      >
                        <Pencil size={18} />
                      </button>

                      <button
                        className="btn-danger btn-small"
                        onClick={() => handleDelete(e.id)}
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  </div>
);
}